import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';

import debounce from 'lodash/debounce';

import PageHeader from './components/page-header';
import { PageContent } from 'src/components/page';
import CompareCarousel from './components/carousel';
import CompareProducts from './components/compare-products';
import StickyPageHeader from '../../components/sticky-page-header';
import ViewportContext from '../../components/viewport/context';

import { adsViewUpdate } from '../../globals/actions';
import { AdsViewUpdate } from '../../globals/actions/types';
import { AppState } from '../../reducers';
import {
  setAddProductAnimation as setAddProductAnimationAction,
  toggleSearchInput as toggleSearchInputAction,
} from './actions';

import { ProductComparePageQuery, RelatedProduct } from '../../types';

type OwnProps = { data: ProductComparePageQuery };

type DispatchProps = {
  adsViewUpdateDispatch: (view: AdsViewUpdate) => void;
  setAddProductAnimation: (state: boolean) => void;
  toggleSearchInput: () => void;
};

type StateProps = {
  showSearchInput: boolean;
};

type Props = OwnProps & DispatchProps & StateProps;

const Compare = ({
  adsViewUpdateDispatch,
  data,
  showSearchInput,
  setAddProductAnimation,
  toggleSearchInput,
}: Props) => {
  const { category, products, related } = data;
  const { isDesktop, isMobile } = useContext(ViewportContext);

  const initialShouldAnimate = false;

  const contentRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const isProductAdded = useRef<boolean>(initialShouldAnimate);

  const [productCount, setProductCount] = useState<number | null>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(
    contentRef && contentRef.current ? contentRef.current.scrollLeft : 0
  );
  const [highlightDifferences, setHighlightDifferences] = useState<boolean>(
    false
  );

  const showSearchInHeader =
    !isDesktop || Boolean(products && products.length > 3);
  const showOverlay = !isDesktop && showSearchInput;

  // We use ref instead of state here because state will not be updated
  // inside event listeners, as it belongs to the initial render only.
  const setProductAdded = (state: boolean) => {
    isProductAdded.current = state;
  };

  const addProductScrollHandler = useCallback(
    debounce(() => {
      if (isProductAdded.current) {
        setAddProductAnimation(true);
        setProductAdded(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    adsViewUpdateDispatch({ view: 'productCompare:mounted' });

    setProductCount(products ? products.length : null);
  }, [adsViewUpdateDispatch, products]);

  const productList = Array.isArray(products)
    ? products.filter(item => Boolean(item))
    : [];

  const scrollToEnd = useCallback(() => {
    if (!contentRef.current) {
      return;
    }

    const newPosition =
      contentRef.current.scrollWidth - contentRef.current.clientWidth;

    contentRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth',
    });

    setScrollPosition(newPosition);
  }, []);

  useEffect(() => {
    if (!productCount || !products) return;
    if (products.length > productCount) {
      setProductAdded(true);
      scrollToEnd();
    }
  }, [productCount, products, scrollToEnd]);

  useEffect(() => {
    const current = contentRef.current;

    if (current) {
      current.addEventListener('scroll', addProductScrollHandler);
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', addProductScrollHandler);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const relatedProducts = (related?.products && related.products.length
    ? related.products
    : []) as RelatedProduct[];

  return products ? (
    <PageContent withAds>
      <PageHeader
        category={category}
        highlightDifferences={highlightDifferences}
        products={productList}
        relatedProducts={relatedProducts}
        setHighlightDifferences={setHighlightDifferences}
        showSearchInHeader={showSearchInHeader}
        showSearchInput={showSearchInput}
        toggleSearchInput={toggleSearchInput}
      />

      {isMobile && Boolean(products.length) && contentRef.current && (
        <StickyPageHeader>
          <CompareCarousel
            clientWidth={contentRef.current.clientWidth}
            products={productList}
            scrollPosition={scrollPosition}
            totalVisibleDots={10}
            totalVisibleItems={2}
          />
        </StickyPageHeader>
      )}

      <CompareProducts
        category={category}
        highlightDifferences={highlightDifferences}
        contentRef={contentRef}
        outerRef={outerRef}
        position={scrollPosition}
        products={productList}
        relatedProducts={relatedProducts}
        setScrollPosition={setScrollPosition}
        showOverlay={showOverlay}
        showSearchInHeader={showSearchInHeader}
      />
    </PageContent>
  ) : null;
};

const mapStateToProps = (state: AppState) => ({
  showSearchInput: state.areas.compare.showSearchInput,
});

const dispatchProps = {
  adsViewUpdateDispatch: adsViewUpdate,
  setAddProductAnimation: setAddProductAnimationAction,
  toggleSearchInput: toggleSearchInputAction,
};

export default connect(mapStateToProps, dispatchProps)(Compare);
